import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Phone, Eye, EyeOff, MessageSquare, Clock, Flag, TrendingUp, UserCog, ToggleLeft, FileCheck, FileText, Building2, Copy, LifeBuoy, Hand, Trash2, Plane } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  PREDICTION_OPTIONS, PREDICTION_LABELS, PREDICTION_LABELS_NUM, VOTE_2022_OPTIONS, VOTE_2022_LABELS,
  STATUS_OPTIONS, STATUS_LABELS, SECTORS, COLORS,
} from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import TimelineView from "@/components/TimelineView";

const LOSS_REASON_OPTIONS = [
  ["rakip_aday", "Rakip aday ilişkisi"],
  ["ortaklik", "Ortaklık/akrabalık"],
  ["memnuniyetsizlik", "Memnuniyetsizlik"],
  ["kararsiz", "Ulaşılamıyor/kararsız"],
  ["diger", "Diğer"],
];

function fmtDate(iso) {  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function VoterEditDialog({ voterId, open, onClose, onSaved, team, sectors }) {
  const { user } = useAuth();
  const [voter, setVoter] = useState(null);
  const [committeeName, setCommitteeName] = useState("");
  const [committees, setCommittees] = useState([]);
  const [respList, setRespList] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [showPhone, setShowPhone] = useState(false);
  const [selPeople, setSelPeople] = useState([]);
  const [docType, setDocType] = useState("munferit");
  const [noPerson, setNoPerson] = useState(false);
  const [itoSicil, setItoSicil] = useState("");
  const [cityVal, setCityVal] = useState("");
  const [tsmVal, setTsmVal] = useState("");
  const [apptDate, setApptDate] = useState("");
  const [apptResp, setApptResp] = useState("");
  const [apptBusy, setApptBusy] = useState(false);
  const [poolBusy, setPoolBusy] = useState(false);
  const [poolNote, setPoolNote] = useState("");
  const [showPoolNote, setShowPoolNote] = useState(false);
  const [poolClaimConfirm, setPoolClaimConfirm] = useState(false);
  const isAgent = user.role === "field_agent" || user.role === "chairman";
  const inPool = !!(voter && voter.pool && voter.pool.added_at);
  const notMine = isAgent && !inPool && voter && voter.team_assignment_2026 !== user.agent_name;
  const isSuperAdmin = user.role === "super_admin";
  const isOwner = !!user.is_owner;
  const cmLocked = !!voter && voter.committee_no === 8 && user.role === "committee_manager" && !inPool;
  const locked = notMine || cmLocked;
  const canSeeNotes = !voter || voter.committee_no !== 8 || isOwner;
  const branchLayer = voter && voter.branch_layer && typeof voter.branch_layer === "object" ? voter.branch_layer : null;
  const isSahis = !!voter && voter.entity_class === "sahis";

  useEffect(() => {
    if (open && voterId) {
      setShowPhone(false);
      api.get(`/voters/${voterId}`).then(({ data }) => {
        setVoter(data);
        if (data?.committee_no != null) {
          api.get("/responsibles", { params: { committee_no: data.committee_no } })
            .then(({ data: rs }) => setRespList(rs.map((r) => r.name)))
            .catch(() => setRespList(null));
          api.get("/committees")
            .then(({ data: cs }) => {
              setCommittees(cs || []);
              setCommitteeName((cs.find((c) => c.committee_no === data.committee_no) || {}).name || "");
            })
            .catch(() => setCommitteeName(""));
        }
      });
    } else {
      setVoter(null);
      setRespList(null);
      setCommitteeName("");
      setNote("");
    }
  }, [open, voterId]);

  const [lossOpen, setLossOpen] = useState(false);
  const [vpOpen, setVpOpen] = useState(false);
  const [vpPeople, setVpPeople] = useState([]);
  const [vpChoice, setVpChoice] = useState("");
  const [vpOther, setVpOther] = useState("");
  const [vpEdit, setVpEdit] = useState(false);
  const [statusLocked, setStatusLocked] = useState(false);
  const [sensWords, setSensWords] = useState(null);
  useEffect(() => {
    api.get("/settings/status-lock").then(({ data }) => setStatusLocked(!!data.locked)).catch(() => {});
  }, []);
  const [holderOpts, setHolderOpts] = useState([]);
  const [holderNeeded, setHolderNeeded] = useState(false);
  const [holderChoice, setHolderChoice] = useState("");
  const [holderOther, setHolderOther] = useState("");
  const [lossReason, setLossReason] = useState("");
  const [lossNote, setLossNote] = useState("");
  const [idForm, setIdForm] = useState({});
  const [idSaving, setIdSaving] = useState(false);
  const [idHistory, setIdHistory] = useState([]);

  const ID_KEYS = ["company_name", "contact_person", "partner_info", "phone", "city",
    "address", "sicil_no", "istanbul_ticaret_sicil_no", "entity_class"];
  const idInitial = useMemo(
    () => (voter ? ID_KEYS.reduce((a, k) => ({ ...a, [k]: (k === "entity_class" ? (voter[k] || "tuzel") : (voter[k] ?? "")) }), {}) : {}),
    [voter], // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => { setIdForm(idInitial); }, [idInitial]);
  const idDirty = ID_KEYS.some((k) => (idForm[k] ?? "") !== (idInitial[k] ?? ""));

  const saveIdentity = async () => {
    const changed = ID_KEYS.reduce((a, k) => (
      (idForm[k] ?? "") !== (idInitial[k] ?? "") ? { ...a, [k]: idForm[k] ?? "" } : a), {});
    if (!Object.keys(changed).length) return;
    setIdSaving(true);
    try {
      const { data } = await api.patch(`/voters/${voterId}`, changed);
      setVoter(data);
      onSaved?.(data);
      loadIdHistory();
      toast.success("Firma bilgileri güncellendi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setIdSaving(false);
    }
  };

  const loadIdHistory = () => {
    if (!isSuperAdmin || !voterId) return;
    api.get(`/voters/${voterId}/identity-history`)
      .then(({ data }) => setIdHistory(data.items || []))
      .catch(() => setIdHistory([]));
  };
  useEffect(loadIdHistory, [voterId, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const revertField = async (h) => {
    setIdSaving(true);
    try {
      const { data } = await api.patch(`/voters/${voterId}`, { [h.field]: h.old ?? "" });
      setVoter(data);
      onSaved?.(data);
      loadIdHistory();
      toast.success(`${h.label} eski değerine döndürüldü`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setIdSaving(false);
    }
  };

  const patch = async (fields) => {
    if (locked) { toast.error(cmLocked ? "8 numaralı komitede değişiklik yapılamaz" : "Bu firma size atanmadığı için değişiklik yapamazsınız"); return; }
    if (fields.current_vote_prediction_2026 === "KARSI"
        && voter?.current_vote_prediction_2026 === "KESIN" && !fields.loss_reason) {
      setLossReason(""); setLossNote(""); setLossOpen(true);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/voters/${voterId}`, fields);
      setVoter(data);
      onSaved?.(data);
      if (data.merkez_pending) {
        toast.info("Seçim Ofisi onayına gönderildi — yetkili ofiste kontrol edip onaylayana kadar belge alınmış sayılmaz");
      } else {
        toast.success("Seçmen güncellendi");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === "voting_person_required" || detail?.error === "document_holder_required") {
        setVpPeople(detail.people || []);
        setVpChoice("");
        setVpOther("");
        setVpEdit(false);
        setHolderOpts(detail.holders || []);
        setHolderNeeded(detail.need_holder !== false);
        setHolderChoice("");
        setHolderOther("");
        setVpOpen(true);
        return;
      }
      toast.error(formatApiError(detail));
    } finally {
      setSaving(false);
    }
  };

  const openVotingEdit = async () => {
    try {
      const { data } = await api.get(`/voters/${voterId}/voting-options`);
      const people = data.people || [];
      setVpPeople(people);
      setHolderOpts(data.holders || []);
      setHolderNeeded(true);
      const cur = data.voting_person || "";
      const known = people.includes(cur);
      setVpChoice(cur ? (known ? cur : "__other__") : "");
      setVpOther(known ? "" : cur);
      const h = data.document_holder || "";
      const hKnown = (data.holders || []).includes(h);
      setHolderChoice(h ? (hKnown ? h : "__other__") : "");
      setHolderOther(hKnown ? "" : h);
      setVpEdit(true);
      setVpOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const confirmVotingPerson = async () => {
    if (vpPeople.length > 0 && !vpChoice) { toast.error("Oy kullanacak yetkiliyi seçin"); return; }
    const vp = vpChoice === "__other__" ? vpOther.trim() : vpChoice;
    if (vpChoice === "__other__" && vp.length < 3) { toast.error("Oy kullanacak kişinin adını yazın"); return; }
    const holder = holderChoice === "__other__" ? holderOther.trim() : holderChoice;
    if (holderNeeded && !holder) { toast.error("Belgenin kimde/nerede saklanacağını seçin"); return; }
    setVpOpen(false);
    await patch({
      ...(vpEdit ? {} : { proxy_document: true }),
      ...(vp ? { voting_person: vp } : {}),
      ...(holder ? { document_holder: holder } : {}),
    });
    setVpEdit(false);
  };

  const confirmLoss = async () => {
    if (!lossReason) { toast.error("Kayıp sebebini seçin"); return; }
    if (lossReason === "diger" && lossNote.trim().length < 3) { toast.error("'Diğer' için kısa bir açıklama yazın"); return; }
    setLossOpen(false);
    await patch({ current_vote_prediction_2026: "KARSI", loss_reason: lossReason, loss_reason_note: lossNote.trim() });
  };

  const addNote = async (confirmSensitive = false) => {
    if (!note.trim()) return;
    if (locked) { toast.error(cmLocked ? "8 numaralı komitede değişiklik yapılamaz" : "Bu firma size atanmadığı için not ekleyemezsiniz"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/voters/${voterId}/notes`, { text: note, confirm_sensitive: confirmSensitive });
      setVoter((v) => ({ ...v, notes_2026: [...(v.notes_2026 || []), data] }));
      setNote("");
      setSensWords(null);
      onSaved?.();
      toast.success("Arama kaydedildi");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "sensitive_note") {
        setSensWords(d.words || []);
      } else {
        toast.error(formatApiError(d));
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleSector = (s) => {
    const cur = voter.sector_tags || [];
    const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
    patch({ sector_tags: next });
  };

  useEffect(() => {
    if (voter) {
      const pl = (voter.contact_person || "").split(",").map((s) => s.trim()).filter(Boolean);
      setSelPeople(pl.slice(0, 1));
      setDocType("munferit");
      setNoPerson(false);
      setItoSicil(voter.istanbul_ticaret_sicil_no || "");
      setCityVal(voter.city || "");
      setTsmVal(voter.tsm_adi || "");
    }
  }, [voter?.id]);

  const togglePerson = (name) => {
    setSelPeople((cur) => {
      const next = cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name];
      setDocType(next.length > 1 ? "musterek" : "munferit");
      return next;
    });
  };

  const downloadYetki = async (fmt) => {
    if (!noPerson && selPeople.length === 0) { toast.error("En az bir kişi seçin veya 'Yetkili seçme' seçeneğini işaretleyin"); return; }
    try {
      const params = new URLSearchParams();
      params.set("type", docType);
      params.set("format", fmt);
      if (noPerson) {
        params.set("blank", "true");
      } else {
        selPeople.forEach((p) => params.append("people", p));
      }
      const res = await api.get(`/voters/${voterId}/yetki-belgesi?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Yetki_Belgesi_${(voter.company_name || "firma").slice(0, 40)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Yetki belgesi dilekçesi indirildi (${fmt.toUpperCase()})`);
    } catch {
      toast.error("Belge oluşturulamadı");
    }
  };

  if (!open) return null;

  const peopleList = (voter?.contact_person || "").split(",").map((s) => s.trim()).filter(Boolean);

  const pool = voter && voter.pool && voter.pool.added_at ? voter.pool : null;
  const iAmInterested = pool ? (pool.interested || []).some((x) => x.user_id === user.id) : false;
  const canRemovePool = pool && (user.is_owner || user.role === "super_admin" || pool.added_by === user.id);

  const addToPool = async () => {
    setPoolBusy(true);
    try {
      const { data } = await api.post(`/voters/${voterId}/pool`, { note: poolNote });
      setVoter((v) => ({ ...v, pool: data }));
      setPoolNote(""); setShowPoolNote(false);
      onSaved?.();
      toast.success("Firma havuza atıldı");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPoolBusy(false); }
  };
  const togglePoolInterest = async () => {
    setPoolBusy(true);
    try {
      const { data } = await api.post(`/voters/${voterId}/pool/interest`, { interested: !iAmInterested });
      setVoter((v) => ({ ...v, pool: { ...v.pool, interested: data.interested } }));
      onSaved?.();
      toast.success(iAmInterested ? "İlgilenme işareti kaldırıldı" : "Bu firmayla ilgilendiğiniz işaretlendi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPoolBusy(false); }
  };
  const removePool = async () => {
    setPoolBusy(true);
    try {
      await api.delete(`/voters/${voterId}/pool`);
      setVoter((v) => ({ ...v, pool: null }));
      onSaved?.();
      toast.success("Firma havuzdan çıkarıldı");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPoolBusy(false); }
  };
  const doPoolClaim = async () => {
    setPoolBusy(true);
    try {
      const { data } = await api.post(`/voters/${voterId}/pool/claim`);
      setVoter((v) => ({ ...v, pool: null, team_assignment_2026: data.responsible }));
      onSaved?.();
      toast.success(`Firma devralındı — sorumlu: ${data.responsible}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPoolBusy(false); setPoolClaimConfirm(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="voter-edit-dialog">
        {!voter ? (
          <>
            <DialogHeader className="sr-only"><DialogTitle>Seçmen yükleniyor</DialogTitle></DialogHeader>
            <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-lg pr-6 flex items-start gap-2">
                <span className="select-text">{voter.company_name}</span>
                <button
                  type="button"
                  data-testid="copy-company-name"
                  title="Ünvanı kopyala"
                  onClick={async () => {
                    const text = voter.company_name || "";
                    const fallback = () => {
                      const ta = document.createElement("textarea");
                      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
                      document.body.appendChild(ta); ta.focus(); ta.select();
                      const ok = document.execCommand("copy"); ta.remove();
                      if (!ok) throw new Error("execCommand failed");
                    };
                    try {
                      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
                      else fallback();
                      toast.success("Ünvan kopyalandı");
                    } catch {
                      try { fallback(); toast.success("Ünvan kopyalandı"); }
                      catch { toast.error("Kopyalanamadı"); }
                    }
                  }}
                  className="shrink-0 mt-1 text-muted-foreground hover:text-[#0F2C59] dark:hover:text-sky-300 transition-colors"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="voter-committee">
                Komite #{voter.committee_no}{committeeName ? ` · ${committeeName}` : ""}
              </p>
              {isSuperAdmin && (
                <div className="mt-2 flex items-center gap-2" data-testid="voter-committee-change">
                  <span className="text-xs text-muted-foreground shrink-0">Komiteyi değiştir</span>
                  <Select
                    value={voter.committee_no != null ? String(voter.committee_no) : ""}
                    onValueChange={(val) => {
                      const no = Number(val);
                      if (no === voter.committee_no) return;
                      const c = committees.find((x) => x.committee_no === no);
                      if (!window.confirm(`Firma #${no} ${c?.name || ""} komitesine taşınacak. Onaylıyor musunuz?`)) return;
                      patch({ committee_no: no });
                    }}
                  >
                    <SelectTrigger className="h-8 w-72 text-xs" data-testid="edit-committee">
                      <SelectValue placeholder="Komite seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {committees.map((c) => (
                        <SelectItem key={c.committee_no} value={String(c.committee_no)}>
                          #{c.committee_no} · {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Sicil #{voter.sicil_no} &bull; {voter.branch} &bull; Kayıt {voter.registration_year}
              </p>
            </DialogHeader>

            <div className="mt-2 rounded-lg border border-border p-3 space-y-2 bg-muted/30" data-testid="voter-yetki-appt">
              <p className="text-xs font-medium text-foreground">Yetki Belgesi Randevusu</p>
              <div className="flex flex-wrap items-center gap-2">
                <Input type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)} className="h-8 w-40" data-testid="voter-appt-date" />
                <select value={apptResp} onChange={(e) => setApptResp(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-sm" data-testid="voter-appt-resp">
                  <option value="">Sorumlu yok</option>
                  {(team || []).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <Button size="sm" disabled={apptBusy || !apptDate} data-testid="voter-appt-create" onClick={async () => {
                  setApptBusy(true);
                  try {
                    await api.post("/yetki-appointments", { voter_id: voterId, date: apptDate, responsible: apptResp || null });
                    toast.success("Yetki belgesi randevusu oluşturuldu");
                    setApptDate(""); setApptResp("");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Oluşturulamadı");
                  } finally { setApptBusy(false); }
                }}>Randevu Ekle</Button>
              </div>
            </div>

            {pool ? (
              <div className="mt-2 rounded-lg border border-cyan-300 dark:border-cyan-900 bg-cyan-50 dark:bg-cyan-950/30 p-3 space-y-2" data-testid="pool-status-box">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-800 dark:text-cyan-300">
                    <LifeBuoy className="h-4 w-4" /> Bu firma havuzda
                  </span>
                  <span className="text-[11px] text-muted-foreground">Ekleyen: {pool.added_by_name}</span>
                </div>
                {pool.note && <p className="text-xs italic text-cyan-900/80 dark:text-cyan-200/80">"{pool.note}"</p>}
                {(pool.interested || []).length > 0 && (
                  <p className="text-[11px] text-muted-foreground" data-testid="pool-interested-names">
                    İlgilenenler: {(pool.interested || []).map((x) => x.name).join(", ")}
                  </p>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" className={iAmInterested ? "h-8 bg-emerald-600 hover:bg-emerald-700" : "h-8 bg-[#0F2C59] hover:bg-[#0F2C59]/90"} disabled={poolBusy} onClick={togglePoolInterest} data-testid="pool-interest-toggle">
                    {poolBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Hand className="h-4 w-4 mr-1.5" />{iAmInterested ? "İlgileniyorum ✓" : "Ben İlgileniyorum"}</>}
                  </Button>
                  {!poolClaimConfirm ? (
                    <Button size="sm" variant="outline" className="h-8 border-emerald-500 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40" disabled={poolBusy} onClick={() => setPoolClaimConfirm(true)} data-testid="pool-claim-open">
                      <Hand className="h-4 w-4 mr-1.5" />Devral
                    </Button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-900 px-2 py-1">
                      <span className="text-xs text-emerald-800 dark:text-emerald-300">Sorumluluğa alınsın mı?</span>
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" disabled={poolBusy} onClick={doPoolClaim} data-testid="pool-claim-confirm-inline">
                        {poolBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Evet, Devral"}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7" disabled={poolBusy} onClick={() => setPoolClaimConfirm(false)} data-testid="pool-claim-cancel-inline">Vazgeç</Button>
                    </span>
                  )}
                  {canRemovePool && (
                    <Button size="sm" variant="ghost" className="h-8 text-destructive" disabled={poolBusy} onClick={removePool} data-testid="pool-remove-btn">
                      <Trash2 className="h-4 w-4 mr-1.5" />Havuzdan Çıkar
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-2" data-testid="pool-add-box">
                {!showPoolNote ? (
                  <Button size="sm" variant="outline" className="h-8 border-cyan-400 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-950/40" onClick={() => setShowPoolNote(true)} data-testid="pool-add-open">
                    <LifeBuoy className="h-4 w-4 mr-1.5" />Havuza At
                  </Button>
                ) : (
                  <div className="rounded-lg border border-cyan-300 dark:border-cyan-900 bg-cyan-50 dark:bg-cyan-950/30 p-3 space-y-2">
                    <p className="text-xs text-cyan-800 dark:text-cyan-300">Ulaşamadınız mı, tanıyan var mı? Kısa bir not ekleyip (opsiyonel) havuza atın.</p>
                    <Textarea value={poolNote} onChange={(e) => setPoolNote(e.target.value)} placeholder="Örn: Ulaşamadım, tanıyan var mı?" rows={2} className="resize-none bg-background" data-testid="pool-note-input" />
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="h-8 bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={poolBusy} onClick={addToPool} data-testid="pool-add-submit">
                        {poolBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LifeBuoy className="h-4 w-4 mr-1.5" />Havuza At</>}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => { setShowPoolNote(false); setPoolNote(""); }} data-testid="pool-add-cancel">Vazgeç</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Tabs defaultValue="duzenle" className="mt-1">
              <TabsList className={`grid ${branchLayer ? "grid-cols-3" : "grid-cols-2"} w-full`}>
                <TabsTrigger value="duzenle" data-testid="tab-edit">Düzenle</TabsTrigger>
                {branchLayer && (
                  <TabsTrigger value="sube" data-testid="tab-branch"><Building2 className="h-4 w-4 mr-1.5" />Şube</TabsTrigger>
                )}
                <TabsTrigger value="timeline" data-testid="tab-timeline"><Clock className="h-4 w-4 mr-1.5" />Zaman Tüneli</TabsTrigger>
              </TabsList>
              <TabsContent value="duzenle">
            <div className="space-y-5">
              {notMine && (
                <div className="text-xs bg-slate-100 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-slate-600 dark:text-slate-300" data-testid="readonly-banner">
                  Bu firma size atanmamış. Yalnızca görüntüleyebilirsiniz; değişiklik yapamazsınız.
                </div>
              )}
              {cmLocked && (
                <div className="text-xs bg-slate-100 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-slate-600 dark:text-slate-300" data-testid="committee8-readonly-banner">
                  8 numaralı komite salt-okunurdur. Bu firmayı görüntüleyebilir ancak değişiklik yapamazsınız.
                </div>
              )}
              {inPool && (
                <div className="text-xs bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-300 dark:border-cyan-900 rounded-lg p-3 text-cyan-800 dark:text-cyan-300" data-testid="pool-open-banner">
                  Bu firma <b>havuzda</b>. Komite/şube ayrımı olmadan herkes veri girişi yapabilir, not ve yorum ekleyebilir.
                </div>
              )}
              {/* Contact — yalnız sistem sahibinde render edilir */}
              {isOwner && (
              <div className="flex items-center gap-3 bg-muted rounded-lg p-3">
                <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Yetkili: {voter.contact_person || "-"}</p>
                  {voter.partner_info && (
                    <p className="text-xs text-muted-foreground" data-testid="partner-value">Ortak: {voter.partner_info}</p>
                  )}
                  {voter.phone && (
                    <p className="text-sm font-medium text-foreground" data-testid="phone-value">
                      {showPhone ? voter.phone : "•••• ••• •• ••"}
                    </p>
                  )}
                  {voter.address && (
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid="address-value">{voter.address}</p>
                  )}
                </div>
                {voter.phone && (
                  <Button variant="ghost" size="sm" onClick={() => setShowPhone((s) => !s)} data-testid="toggle-phone-visibility">
                    {showPhone ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                )}
                {showPhone && voter.phone && (
                  <Button size="sm" asChild className="bg-emerald-600 hover:bg-emerald-700">
                    <a href={`tel:${voter.phone.replace(/\s/g, "")}`} data-testid="call-link"><Phone className="h-4 w-4" /></a>
                  </Button>
                )}
              </div>
              )}
              {!isOwner && (voter.contact_person || voter.partner_info) && (
                <div className="flex items-start gap-3 bg-muted rounded-lg p-3" data-testid="voter-people-box">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Yetkili: {voter.contact_person || "-"}</p>
                    {voter.partner_info && (
                      <p className="text-xs text-muted-foreground" data-testid="partner-value">Ortak: {voter.partner_info}</p>
                    )}
                  </div>
                </div>
              )}

              {isSuperAdmin && (
                <div className="rounded-lg border border-amber-300 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-3" data-testid="identity-edit-section">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">Firma bilgilerini düzenle</p>
                    {idSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      ["company_name", "Ünvan", "sm:col-span-2"],
                      ["contact_person", "Yetkili kişi(ler) — virgülle ayırın", "sm:col-span-2"],
                      ["partner_info", "Ortak(lar)", "sm:col-span-2"],
                      ...(isOwner ? [["phone", "Telefon", ""]] : []),
                      ["city", "Şehir", ""],
                      ["address", "Adres", "sm:col-span-2"],
                      ["sicil_no", "Sicil No", ""],
                      ["istanbul_ticaret_sicil_no", "İТО Ticaret Sicil No", ""],
                    ].filter(Boolean).map(([k, label, cls]) => (
                      <div key={k} className={`space-y-1 ${cls}`}>
                        <Label className="text-xs">{label}</Label>
                        <Input value={idForm[k] ?? ""} onChange={(e) => setIdForm((s) => ({ ...s, [k]: e.target.value }))}
                          className="h-9 bg-background" data-testid={`identity-${k}`} />
                      </div>
                    ))}
                    {isOwner && (
                      <div className="space-y-1">
                        <Label className="text-xs">Firma Tipi</Label>
                        <Select value={idForm.entity_class || "tuzel"}
                          onValueChange={(v) => setIdForm((s) => ({ ...s, entity_class: v }))}>
                          <SelectTrigger className="h-9 bg-background" data-testid="identity-entity_class"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tuzel">Tüzel Kişi — yetki belgesi gerekli</SelectItem>
                            <SelectItem value="sahis">Şahıs Firması — belgeden muaf</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={saveIdentity} disabled={idSaving || !idDirty} data-testid="identity-save">Bilgileri Kaydet</Button>
                    <Button size="sm" variant="outline" onClick={() => setIdForm(idInitial)} disabled={idSaving || !idDirty} data-testid="identity-reset">Geri Al</Button>
                    <span className="text-[11px] text-muted-foreground">Her değişiklik işlem günlüğüne kaydedilir.</span>
                  </div>
                  {!!idHistory.length && (
                    <div className="pt-2 border-t border-amber-200 dark:border-amber-900/60 space-y-1.5" data-testid="identity-history">
                      <p className="text-xs font-medium">Son bilgi değişiklikleri</p>
                      {idHistory.map((h, i) => (
                        <div key={i} className="flex items-start justify-between gap-2 text-[11px]">
                          <div className="min-w-0">
                            <span className="font-medium">{h.label}:</span>{" "}
                            <span className="text-muted-foreground break-words">{h.old || "boş"} → {h.new || "boş"}</span>
                            <span className="text-muted-foreground"> · {h.actor || "—"} · {String(h.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                          </div>
                          <button onClick={() => revertField(h)} disabled={idSaving}
                            className="shrink-0 font-medium text-[#0F2C59] dark:text-sky-300 hover:underline disabled:opacity-50"
                            data-testid={`identity-revert-${i}`}>Eski değere dön</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>2026 Tahmini</Label>
                  <Select value={voter.current_vote_prediction_2026 || ""} onValueChange={(v) => patch({ current_vote_prediction_2026: v })} disabled={locked}>
                    <SelectTrigger data-testid="edit-prediction"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      {PREDICTION_OPTIONS.map((p) => <SelectItem key={p} value={p}>{PREDICTION_LABELS_NUM[p]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {voter.loss_reason_label && voter.current_vote_prediction_2026 === "KARSI" && (
                    <p className="text-[11px] text-red-600" data-testid="voter-loss-reason">
                      Kayıp sebebi: {voter.loss_reason_label}{voter.loss_reason_note ? ` — ${voter.loss_reason_note}` : ""}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5">
                    Durum
                    {statusLocked && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" data-testid="status-locked-badge">Kilitli</span>}
                  </Label>
                  <Select value={voter.status || ""} onValueChange={(v) => patch({ status: v })} disabled={isAgent || cmLocked || statusLocked}>
                    <SelectTrigger data-testid="edit-status"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {statusLocked && <p className="text-[11px] text-muted-foreground">ASKI/FAAL kilitli. Ayarlar &gt; Durum Kilidi'nden kapatılabilir.</p>}
                </div>

                <div className="space-y-1.5">
                  <Label>2022 Sonucu</Label>
                  <Select value={voter.historical_vote_2022 || ""} onValueChange={(v) => patch({ historical_vote_2022: v })} disabled={!isSuperAdmin || isAgent || cmLocked}>
                    <SelectTrigger data-testid="edit-vote2022"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      {VOTE_2022_OPTIONS.map((v) => <SelectItem key={v} value={v}>{VOTE_2022_LABELS[v]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>2026 Sorumlu</Label>
                  <Select value={voter.team_assignment_2026 || ""} onValueChange={(v) => patch({ team_assignment_2026: v })} disabled={isAgent || cmLocked}>
                    <SelectTrigger data-testid="edit-caller"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const opts = respList != null ? respList : (team || []);
                        const cur = voter.team_assignment_2026;
                        const list = cur && !opts.includes(cur) ? [cur, ...opts] : opts;
                        return list.length
                          ? list.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)
                          : <div className="px-2 py-1.5 text-xs text-muted-foreground">Bu komiteye atanmış sorumlu yok</div>;
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Sektör Etiketleri</Label>
                <div className="flex flex-wrap gap-2">
                  {(sectors && sectors.length ? sectors : SECTORS).map((s) => {
                    const active = (voter.sector_tags || []).includes(s);
                    return (
                      <button key={s} onClick={() => toggleSector(s)} disabled={isAgent || cmLocked}
                        data-testid={`sector-tag-${s}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-background text-muted-foreground border-border hover:border-[#0F2C59]"}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-emerald-300 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-3" data-testid="proxy-row">
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileCheck className="h-5 w-5 text-emerald-600 shrink-0" />
                  <div className="min-w-0">
                    <Label className="text-emerald-800 dark:text-emerald-300">{isSahis ? "%90 Gelecek" : "Yetki Belgesi Alındı"}</Label>
                    <p className="text-xs text-emerald-700/80 dark:text-emerald-400/80">
                      {isSahis
                        ? "Şahıs şirketi: yetki belgesi gerekmez, kimlikle oy kullanır. İşaretlenirse belge almış gibi %90 katılım varsayılır."
                        : "Alındıysa firma güvence altındaki (KESİN) oya sayılır."}
                    </p>
                  </div>
                </div>
                <Switch checked={!!voter.proxy_document} onCheckedChange={(v) => patch({ proxy_document: v })} disabled={saving || locked} data-testid="edit-proxy" />
              </div>

              <div className="text-sm rounded-lg border border-border px-3 py-2 flex flex-wrap items-center gap-x-1.5 gap-y-1" data-testid="voting-summary">
                <span className="text-muted-foreground">Oy kullanacak kişi:</span>
                <button type="button" disabled={locked} onClick={openVotingEdit} data-testid="voting-summary-person"
                  className="font-bold text-[#0F2C59] dark:text-sky-300 underline underline-offset-2 decoration-dotted hover:opacity-80 disabled:no-underline">
                  {voter.voting_person || "belirlenmedi"}
                </button>
                <span className="text-muted-foreground">, Yetki belgesi:</span>
                <button type="button" disabled={locked} onClick={openVotingEdit} data-testid="voting-summary-holder"
                  className="font-bold text-emerald-700 dark:text-emerald-300 underline underline-offset-2 decoration-dotted hover:opacity-80 disabled:no-underline">
                  {voter.document_holder || "belirlenmedi"}
                </button>
                {voter.merkez_pending && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" data-testid="merkez-pending-badge">
                    Seçim Ofisi onayı bekliyor
                  </span>
                )}
                {voter.merkez_rejected_reason && !voter.merkez_pending && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" data-testid="merkez-rejected-badge"
                    title={voter.merkez_rejected_reason}>
                    Merkez reddetti: {voter.merkez_rejected_reason}
                  </span>
                )}
              </div>

              {!isSahis && (
              <div className="rounded-lg border border-border p-3 space-y-3" data-testid="yetki-belge-section">
                <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#0F2C59] dark:text-sky-300" /><Label>Yetki Belgesi Dilekçesi (Word)</Label></div>
                <p className="text-xs text-muted-foreground">Ticaret ünvanı ve İTO Ticaret Sicil No dilekçeye otomatik gelir; TC kimlik no boş bırakılır. Bugünün tarihi eklenir. Dilekçedeki kişiyi/kişileri seçin.</p>

                <div className="space-y-1.5 rounded-md bg-muted/50 p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs">İstanbul Ticaret Sicil No (İTO)</Label>
                    <button type="button" data-testid="ito-search-link"
                      onClick={async () => {
                        try { await navigator.clipboard.writeText(voter.company_name || ""); toast.success("Ünvan panoya kopyalandı — İTO arama kutusuna yapıştırın"); }
                        catch { toast.info("İTO açılıyor — firmayı ünvanla aratın"); }
                        window.open("https://bilgibankasi.ito.org.tr/tr/bilgi-bankasi/firma-bilgileri", "_blank", "noopener,noreferrer");
                      }}
                      className="text-xs text-[#0F2C59] dark:text-sky-300 underline underline-offset-2 hover:opacity-80">
                      Ünvanı kopyala &amp; İTO'da Ara →
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input value={itoSicil} onChange={(e) => setItoSicil(e.target.value)}
                      onBlur={() => { const val = itoSicil.trim(); if (val !== (voter.istanbul_ticaret_sicil_no || "")) patch({ istanbul_ticaret_sicil_no: val }); }}
                      placeholder="Örn: 123456" disabled={locked} className="h-9" data-testid="ito-sicil-input" />
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Firma ünvanını panoya kopyalar ve İTO Bilgi Bankası'nı yeni sekmede açar; arama kutusuna yapıştırıp bulduğunuz <b>Ticaret Sicil No</b>'yu buraya girin. (Sistemdeki DTO sicil no'sundan farklıdır.)
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Şehir</Label>
                    <Input value={cityVal} onChange={(e) => setCityVal(e.target.value)}
                      onBlur={() => { const val = cityVal.trim(); if (val !== (voter.city || "")) patch({ city: val }); }}
                      placeholder="Örn: İSTANBUL" disabled={locked} className="h-9" data-testid="edit-city-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">TSM Adı (Sicil Müdürlüğü)</Label>
                    <Input value={tsmVal} onChange={(e) => setTsmVal(e.target.value)}
                      onBlur={() => { const val = tsmVal.trim(); if (val !== (voter.tsm_adi || "")) patch({ tsm_adi: val }); }}
                      placeholder="Örn: İSTANBUL TİCARET SİCİLİ MÜDÜRLÜĞÜ" disabled={locked} className="h-9" data-testid="edit-tsm-input" />
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-snug sm:col-span-2">
                    Dilekçe başlığı <b>TSM Adı</b>'na göre hazırlanır (sonuna "NE" eklenir). TSM Adı boşsa Şehir, o da boşsa İstanbul kullanılır.
                  </p>
                </div>
                {peopleList.length === 0 ? (
                  <p className="text-xs text-amber-600">Bu firmada kayıtlı yetkili/ortak bulunmuyor.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {peopleList.map((p) => (
                      <button key={p} type="button" disabled={noPerson} onClick={() => togglePerson(p)} data-testid={`yetki-person-${p}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${noPerson ? "opacity-40 pointer-events-none" : ""} ${selPeople.includes(p) ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-background text-muted-foreground border-border hover:border-[#0F2C59]"}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="yetki-no-person-toggle">
                  <input type="checkbox" checked={noPerson} onChange={(e) => setNoPerson(e.target.checked)} className="h-3.5 w-3.5 accent-[#0F2C59]" />
                  Yetkili seçme (yetkili kısmı boş kalsın)
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger className="h-9 w-60" data-testid="yetki-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="munferit">Münferit Yetkili (Tek İmza)</SelectItem>
                      <SelectItem value="musterek">Müşterek Yetkili</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => downloadYetki("docx")} disabled={!noPerson && selPeople.length === 0} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="yetki-download">
                    <FileText className="h-4 w-4 mr-2" /> Word İndir
                  </Button>
                  <Button onClick={() => downloadYetki("pdf")} disabled={!noPerson && selPeople.length === 0} variant="outline" data-testid="yetki-download-pdf">
                    <FileText className="h-4 w-4 mr-2" /> PDF İndir
                  </Button>
                </div>
              </div>
              )}

              {canSeeNotes && (<>
              {/* Ulasim organizasyonu: sube firmalarinin gelis/donus tarihleri */}
              <div className="rounded-lg border border-border p-3 space-y-2" data-testid="travel-plan-box">
                <Label className="flex items-center gap-2 text-sm"><Plane className="h-4 w-4" /> Ulaşım Planı</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Geliş Tarihi</Label>
                    <Input type="date" value={voter.arrival_date || ""} disabled={locked}
                      onChange={(e) => patch(e.target.value ? { arrival_date: e.target.value } : { clear_fields: ["arrival_date"] })}
                      className="h-9" data-testid="edit-arrival-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Dönüş Tarihi</Label>
                    <Input type="date" value={voter.return_date || ""} disabled={locked}
                      onChange={(e) => patch(e.target.value ? { return_date: e.target.value } : { clear_fields: ["return_date"] })}
                      className="h-9" data-testid="edit-return-date" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Ulaşım Şekli</Label>
                    <select value={voter.travel_mode || ""} disabled={locked}
                      onChange={(e) => patch(e.target.value ? { travel_mode: e.target.value } : { clear_fields: ["travel_mode"] })}
                      data-testid="edit-travel-mode"
                      className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
                      <option value="">—</option>
                      <option value="ucak">Uçak</option>
                      <option value="otobus">Otobüs</option>
                      <option value="arac">Özel araç</option>
                      <option value="tren">Tren</option>
                      <option value="diger">Diğer</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Karşılama Sorumlusu</Label>
                    <Input defaultValue={voter.travel_greeter || ""} disabled={locked}
                      placeholder="Karşılayacak kişi"
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (voter.travel_greeter || "")) patch(v ? { travel_greeter: v } : { clear_fields: ["travel_greeter"] }); }}
                      className="h-9" data-testid="edit-travel-greeter" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Ulaşım Notu</Label>
                  <Input defaultValue={voter.travel_note || ""} disabled={locked}
                    placeholder="Uçak / otobüs, saat, karşılama vb."
                    onBlur={(e) => { const v = e.target.value.trim(); if (v !== (voter.travel_note || "")) patch(v ? { travel_note: v } : { clear_fields: ["travel_note"] }); }}
                    className="h-9" data-testid="edit-travel-note" />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Şubeden gelecek kişilerin ulaşım organizasyonu için kullanılır. Girilen tarihler <b>Seyahat Planı</b> ekranında ve komite sayfasının <b>Seyahat Planı</b> sekmesinde gün gün listelenir.
                </p>
              </div>

              {voter.notes_2022 && (
                <div className="text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3">
                  <span className="font-semibold text-amber-700 dark:text-amber-400">2022 Notu: </span>
                  <span className="text-amber-800 dark:text-amber-300">{voter.notes_2022}</span>
                </div>
              )}

              {/* Call log */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Arama Kayıtları (2026)</Label>
                <ScrollArea className="max-h-72 rounded-lg border border-border">
                  <div className="p-3 space-y-3">
                    {(voter.notes_2026 || []).length === 0 && <p className="text-xs text-muted-foreground">Henüz kayıt yok.</p>}
                    {(voter.notes_2026 || []).slice().reverse().map((n) => (
                      <div key={n.id} className="text-sm border-l-2 pl-3" style={{ borderColor: COLORS.cyan }}>
                        <p className="text-foreground whitespace-pre-wrap break-words">{n.text}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{n.author} &bull; {fmtDate(n.date)}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <div className="flex gap-2">
                  <Textarea value={note} onChange={(e) => { setNote(e.target.value); setSensWords(null); }} placeholder="Yeni arama notu ekleyin..." rows={2} className="resize-none" disabled={locked} data-testid="new-note-input" />
                  <Button onClick={() => addNote(false)} disabled={saving || !note.trim() || locked} className="self-end bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="add-note-button">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                {sensWords && (
                  <div className="rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2" data-testid="sensitive-note-warning">
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                      <b>KVKK uyarısı:</b> Bu notta özel nitelikli veri olabilecek ifade(ler) var:{" "}
                      <b>{sensWords.join(", ")}</b>. Sağlık, din, etnik köken, siyasi görüş, ceza
                      mahkûmiyeti ve özel hayat bilgisi kaydetmek yasaktır. Notu düzeltmeniz önerilir.
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setSensWords(null)} data-testid="sensitive-note-edit">Notu düzelteyim</Button>
                      <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700" onClick={() => addNote(true)} disabled={saving} data-testid="sensitive-note-force">Sorumluluğu kabul ederek kaydet</Button>
                    </div>
                  </div>
                )}
              </div>
              </>)}
              {!canSeeNotes && (
                <div className="text-xs bg-slate-100 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-slate-600 dark:text-slate-300" data-testid="committee8-notes-hidden">
                  Bu komitenin notları görüntülenemez.
                </div>
              )}
            </div>
              </TabsContent>
              {branchLayer && (
                <TabsContent value="sube">
                  <div className="space-y-4" data-testid="branch-layer-view">
                    <div className="text-xs bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 rounded-lg p-3 text-sky-800 dark:text-sky-300">
                      <b>{voter.branch}</b> şubesindeki bağımsız oy eğilimi merkez seçimden ayrıdır ve yalnızca şube temsilcisi tarafından düzenlenir (salt-okunur). Aşağıda merkez ile şube tercihleri yan yana; <span className="font-semibold">farklı olanlar sarı ile vurgulanır</span>.
                    </div>
                    {(() => {
                      const fmtPred = (x) => (x ? (PREDICTION_LABELS_NUM[x] || x) : "—");
                      const fmtStat = (x) => (x ? (STATUS_LABELS[x] || x) : "—");
                      const rows = [
                        { key: "pred", label: "2026 Tahmini", c: fmtPred(voter.current_vote_prediction_2026), b: fmtPred(branchLayer.current_vote_prediction_2026), diff: (voter.current_vote_prediction_2026 || "") !== (branchLayer.current_vote_prediction_2026 || "") },
                        { key: "status", label: "Durum", c: fmtStat(voter.status), b: fmtStat(branchLayer.status), diff: (voter.status || "") !== (branchLayer.status || "") },
                        { key: "resp", label: "Sorumlu", c: voter.team_assignment_2026 || "—", b: branchLayer.team_assignment_2026 || "—", diff: (voter.team_assignment_2026 || "") !== (branchLayer.team_assignment_2026 || "") },
                        { key: "proxy", label: "Yetki Belgesi", c: voter.proxy_document ? "Alındı" : "—", b: branchLayer.proxy_document ? "Alındı" : "—", diff: (!!voter.proxy_document) !== (!!branchLayer.proxy_document) },
                      ];
                      const predConflict = rows[0].diff;
                      return (
                        <>
                          {predConflict && (
                            <div className="flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-900 rounded-lg p-3 text-amber-800 dark:text-amber-300" data-testid="branch-conflict-alert">
                              <Flag className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                              <span><b>Çelişki:</b> Bu firmanın şubedeki 2026 eğilimi (<b>{fmtPred(branchLayer.current_vote_prediction_2026)}</b>) merkezdekinden (<b>{fmtPred(voter.current_vote_prediction_2026)}</b>) farklı.</span>
                            </div>
                          )}
                          <div className="rounded-lg border border-border overflow-hidden" data-testid="branch-compare">
                            <div className="grid grid-cols-3 bg-muted/50 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              <div className="px-3 py-2">Alan</div>
                              <div className="px-3 py-2">Merkez</div>
                              <div className="px-3 py-2">Şube</div>
                            </div>
                            {rows.map((r) => (
                              <div key={r.key} data-testid={`branch-cmp-${r.key}`} className={`grid grid-cols-3 text-sm border-t border-border ${r.diff ? "bg-amber-50 dark:bg-amber-950/30" : ""}`}>
                                <div className="px-3 py-2 text-muted-foreground flex items-center gap-1.5">
                                  {r.diff && <Flag className="h-3.5 w-3.5 text-amber-600 shrink-0" />}{r.label}
                                </div>
                                <div className="px-3 py-2 text-foreground">{r.c}</div>
                                <div className={`px-3 py-2 font-medium ${r.diff ? "text-amber-700 dark:text-amber-400" : "text-foreground"}`}>{r.b}</div>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-xs text-muted-foreground"><MessageSquare className="h-4 w-4" /> Şube Arama Kayıtları</Label>
                      <ScrollArea className="max-h-72 rounded-lg border border-border">
                        <div className="p-3 space-y-3">
                          {(branchLayer.notes_2026 || []).length === 0 && <p className="text-xs text-muted-foreground">Henüz kayıt yok.</p>}
                          {(branchLayer.notes_2026 || []).slice().reverse().map((n) => (
                            <div key={n.id} className="text-sm border-l-2 pl-3" style={{ borderColor: COLORS.cyan }}>
                              <p className="text-foreground whitespace-pre-wrap break-words">{n.text}</p>
                              <p className="text-[11px] text-muted-foreground mt-0.5">{n.author} &bull; {fmtDate(n.date)}</p>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  </div>
                </TabsContent>
              )}
              <TabsContent value="timeline">
                <TimelineView voterId={voterId} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
      <Dialog open={vpOpen} onOpenChange={setVpOpen}>
        <DialogContent className="sm:max-w-md" data-testid="voting-person-dialog">
          <DialogHeader>
            <DialogTitle>{vpPeople.length > 0 ? "Oy kullanacak yetkili ve belge sahibi" : "Yetki belgesi kimde/nerede?"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {vpPeople.length > 0 && (<>
            <p className="text-sm text-muted-foreground">
              Bu firmada {vpPeople.length} yetkili/ortak kayıtlı (müşterek temsil). Seçim günü sandığa gelecek
              yetkiliyi seçin; komite listelerinde bu isim görünecek.
            </p>
            <div className="space-y-1.5">
              <Label>Yetkili</Label>
              <Select value={vpChoice} onValueChange={setVpChoice}>
                <SelectTrigger data-testid="voting-person-select"><SelectValue placeholder="Yetkili seçin" /></SelectTrigger>
                <SelectContent>
                  {vpPeople.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  <SelectItem value="__other__">Diğer (serbest yazın)</SelectItem>
                </SelectContent>
              </Select>
              {vpChoice === "__other__" && (
                <Input value={vpOther} onChange={(e) => setVpOther(e.target.value)}
                  placeholder="Oy kullanacak kişinin adı soyadı" data-testid="voting-person-other" />
              )}
            </div>
            </>)}
            {holderNeeded && (
              <div className="space-y-1.5">
                <Label>Belge kimde/nerede saklanacak?</Label>
                <Select value={holderChoice} onValueChange={setHolderChoice}>
                  <SelectTrigger data-testid="document-holder-select"><SelectValue placeholder="Seçin (sorumlu / Seçim Ofisi / Şube)" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {holderOpts.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    <SelectItem value="__other__">Diğer (serbest yazın)</SelectItem>
                  </SelectContent>
                </Select>
                {holderChoice === "__other__" && (
                  <Input value={holderOther} onChange={(e) => setHolderOther(e.target.value)}
                    placeholder="Örn. Avukat Bürosu, Genel Sekreterlik..." data-testid="document-holder-other" />
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setVpOpen(false)} data-testid="voting-person-cancel">Vazgeç</Button>
              <Button className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" onClick={confirmVotingPerson} data-testid="voting-person-save">Kaydet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={lossOpen} onOpenChange={setLossOpen}>
        <DialogContent className="sm:max-w-md" data-testid="loss-reason-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Kesin oy karşıya dönüyor — sebep nedir?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Kaybın nedenini kaydedelim; Günlük Rapor'daki kritik hareketlerde görünecek.
            </p>
            <div className="space-y-1.5">
              <Label>Sebep</Label>
              <Select value={lossReason} onValueChange={setLossReason}>
                <SelectTrigger data-testid="loss-reason-select"><SelectValue placeholder="Sebep seçin" /></SelectTrigger>
                <SelectContent>
                  {LOSS_REASON_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Açıklama {lossReason === "diger" ? "(zorunlu)" : "(isteğe bağlı)"}</Label>
              <Textarea rows={3} value={lossNote} onChange={(e) => setLossNote(e.target.value)} placeholder="Kısa açıklama..." data-testid="loss-reason-note" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLossOpen(false)} data-testid="loss-reason-cancel">Vazgeç</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={confirmLoss} data-testid="loss-reason-save">Kaydet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
